import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import MainLayout from '@/components/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, 
  Package, 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Download,
  Mail,
  MessageSquare,
  Check,
  AlertCircle,
  Loader2,
  Building2,
  CreditCard,
  Receipt,
  IndianRupee
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BillingPage = () => {
  const { getAuthHeaders } = useAuth();
  
  // ============== INVOICE SETTINGS STATE (Metadata only) ==============
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [invoiceSettingsLoading, setInvoiceSettingsLoading] = useState(true);
  const [invoiceSettingsForm, setInvoiceSettingsForm] = useState({
    clinic_name: '',
    therapist_name: '',
    business_address: '',
    contact_phone: '',
    contact_email: '',
    gst_number: '',
    invoice_prefix: 'INV',
    bank_name: '',
    bank_account_number: '',
    bank_ifsc: '',
    upi_id: ''
  });
  const [savingInvoiceSettings, setSavingInvoiceSettings] = useState(false);
  
  // ============== BILLING SETTINGS STATE (Pricing defaults) ==============
  const [billingSettings, setBillingSettings] = useState(null);
  const [billingSettingsLoading, setBillingSettingsLoading] = useState(true);
  const [billingSettingsForm, setBillingSettingsForm] = useState({
    default_session_fee: '',
    default_intro_fee: '',
    currency: 'INR'
  });
  const [savingBillingSettings, setSavingBillingSettings] = useState(false);
  
  // ============== PACKAGES STATE ==============
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({
    name: '',
    session_count: 6,
    price: 0,
    discount_percentage: 0,
    description: ''
  });
  const [savingPackage, setSavingPackage] = useState(false);
  
  // ============== INVOICES STATE ==============
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState('');

  // Filter invoices by patient name (client-side) — memoized
  const filteredInvoices = useMemo(() => invoices.filter(invoice => {
    if (!invoiceSearchQuery.trim()) return true;
    const query = invoiceSearchQuery.toLowerCase();
    return (
      (invoice.patient_name && invoice.patient_name.toLowerCase().includes(query)) ||
      (invoice.invoice_number && invoice.invoice_number.toLowerCase().includes(query))
    );
  }), [invoices, invoiceSearchQuery]);

  // Active packages — memoized
  const activePackages = useMemo(() => packages.filter(p => p.is_active), [packages]);

  // ============== FETCH INVOICE SETTINGS (Metadata) ==============
  useEffect(() => {
    const fetchInvoiceSettings = async () => {
      try {
        const response = await fetch(`${API}/api/invoice-settings`, {
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data) {
            setInvoiceSettings(data);
            setInvoiceSettingsForm({
              clinic_name: data.clinic_name || '',
              therapist_name: data.therapist_name || '',
              business_address: data.business_address || '',
              contact_phone: data.contact_phone || '',
              contact_email: data.contact_email || '',
              gst_number: data.gst_number || '',
              invoice_prefix: data.invoice_prefix || 'INV',
              bank_name: data.bank_name || '',
              bank_account_number: data.bank_account_number || '',
              bank_ifsc: data.bank_ifsc || '',
              upi_id: data.upi_id || ''
            });
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch invoice settings:', error);
      } finally {
        setInvoiceSettingsLoading(false);
      }
    };
    
    fetchInvoiceSettings();
  }, [getAuthHeaders]);

  // ============== FETCH BILLING SETTINGS (Pricing) ==============
  useEffect(() => {
    const fetchBillingSettings = async () => {
      try {
        const response = await fetch(`${API}/api/billing-settings`, {
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data) {
            setBillingSettings(data);
            setBillingSettingsForm({
              default_session_fee: data.default_session_fee || '',
              default_intro_fee: data.default_intro_fee || '',
              currency: data.currency || 'INR'
            });
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch billing settings:', error);
      } finally {
        setBillingSettingsLoading(false);
      }
    };
    
    fetchBillingSettings();
  }, [getAuthHeaders]);

  // Fetch packages
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${API}/api/packages`, {
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          setPackages(data);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch packages:', error);
      } finally {
        setPackagesLoading(false);
      }
    };
    
    fetchPackages();
  }, [getAuthHeaders]);

  // Fetch invoices
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        if (process.env.NODE_ENV !== 'production') console.log('[INVOICES-FETCH] Starting fetch...');
        const response = await fetch(`${API}/api/invoices`, {
          headers: getAuthHeaders()
        });
        
        if (process.env.NODE_ENV !== 'production') console.log('[INVOICES-FETCH] Response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          if (process.env.NODE_ENV !== 'production') console.log('[INVOICES-FETCH] Invoices received:', data.length, data);
          setInvoices(data);
        } else {
          const errorText = await response.text();
          if (process.env.NODE_ENV !== 'production') console.error('[INVOICES-FETCH] Error response:', errorText);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('[INVOICES-FETCH] Failed to fetch invoices:', error);
      } finally {
        setInvoicesLoading(false);
      }
    };
    
    fetchInvoices();
  }, [getAuthHeaders]);

  // ============== SAVE INVOICE SETTINGS (Metadata only) ==============
  const handleSaveInvoiceSettings = async () => {
    if (!invoiceSettingsForm.clinic_name || !invoiceSettingsForm.therapist_name || 
        !invoiceSettingsForm.business_address || !invoiceSettingsForm.contact_phone || 
        !invoiceSettingsForm.contact_email) {
      toast.error('Please fill in all required business details');
      return;
    }
    if (!invoiceSettingsForm.bank_name || !invoiceSettingsForm.bank_account_number || 
        !invoiceSettingsForm.bank_ifsc) {
      toast.error('Bank details (Name, Account Number, IFSC) are mandatory');
      return;
    }
    if (!invoiceSettingsForm.upi_id) {
      toast.error('UPI ID is mandatory');
      return;
    }
    
    setSavingInvoiceSettings(true);
    try {
      const method = invoiceSettings ? 'PUT' : 'POST';
      const response = await fetch(`${API}/api/invoice-settings`, {
        method,
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(invoiceSettingsForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvoiceSettings(data);
        toast.success('Invoice settings saved successfully');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to save invoice settings');
      }
    } catch (error) {
      toast.error('Error saving invoice settings');
    } finally {
      setSavingInvoiceSettings(false);
    }
  };

  // ============== SAVE BILLING SETTINGS (Pricing defaults) ==============
  const handleSaveBillingSettings = async () => {
    const sessionFee = parseFloat(billingSettingsForm.default_session_fee);
    const introFee = parseFloat(billingSettingsForm.default_intro_fee);
    
    // Client-side validation: fees must be > 0
    if (!sessionFee || sessionFee <= 0) {
      toast.error('Default session fee must be greater than 0');
      return;
    }
    if (!introFee || introFee <= 0) {
      toast.error('Default intro fee must be greater than 0');
      return;
    }
    
    setSavingBillingSettings(true);
    try {
      const method = billingSettings ? 'PUT' : 'POST';
      const payload = {
        default_session_fee: sessionFee,
        default_intro_fee: introFee,
        currency: billingSettingsForm.currency || 'INR'
      };
      
      const response = await fetch(`${API}/api/billing-settings`, {
        method,
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        setBillingSettings(data);
        toast.success('Pricing defaults saved successfully');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to save pricing defaults');
      }
    } catch (error) {
      toast.error('Error saving pricing defaults');
    } finally {
      setSavingBillingSettings(false);
    }
  };

  // Save package
  const handleSavePackage = async () => {
    if (!packageForm.name || !packageForm.session_count || !packageForm.price) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSavingPackage(true);
    try {
      const url = editingPackage 
        ? `${API}/api/packages/${editingPackage.package_id}`
        : `${API}/api/packages`;
      
      const response = await fetch(url, {
        method: editingPackage ? 'PUT' : 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(packageForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        if (editingPackage) {
          setPackages(packages.map(p => p.package_id === data.package_id ? data : p));
        } else {
          setPackages([...packages, data]);
        }
        toast.success(editingPackage ? 'Package updated' : 'Package created');
        setPackageDialogOpen(false);
        resetPackageForm();
      } else {
        toast.error('Failed to save package');
      }
    } catch (error) {
      toast.error('Error saving package');
    } finally {
      setSavingPackage(false);
    }
  };

  // Delete package
  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    
    try {
      const response = await fetch(`${API}/api/packages/${packageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setPackages(packages.filter(p => p.package_id !== packageId));
        toast.success('Package deleted');
      } else {
        toast.error('Failed to delete package');
      }
    } catch (error) {
      toast.error('Error deleting package');
    }
  };

  // Download invoice PDF
  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await fetch(`${API}/api/invoices/${invoiceId}/pdf`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        toast.error('Failed to download invoice');
      }
    } catch (error) {
      toast.error('Error downloading invoice');
    }
  };

  // Resend invoice
  const handleResendInvoice = async (invoiceId) => {
    try {
      const response = await fetch(`${API}/api/invoices/${invoiceId}/resend`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (data.email_sent) {
        toast.success('Invoice resent successfully');
      } else {
        toast.error(data.error || 'Failed to resend invoice');
      }
    } catch (error) {
      toast.error('Error resending invoice');
    }
  };

  const resetPackageForm = () => {
    setEditingPackage(null);
    setPackageForm({
      name: '',
      session_count: 6,
      price: 0,
      discount_percentage: 0,
      description: ''
    });
  };

  const openEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name,
      session_count: pkg.session_count,
      price: pkg.price,
      discount_percentage: pkg.discount_percentage || 0,
      description: pkg.description || ''
    });
    setPackageDialogOpen(true);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground" style={{ fontFamily: 'Fraunces, serif' }}>
            Billing & Invoicing
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage pricing defaults, invoice settings, packages, and view generated invoices
          </p>
        </div>

        <Tabs defaultValue="settings" className="space-y-6">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="settings" className="gap-2" data-testid="tab-settings">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="packages" className="gap-2" data-testid="tab-packages">
              <Package className="h-4 w-4" />
              Packages
            </TabsTrigger>
            <TabsTrigger value="invoices" className="gap-2" data-testid="tab-invoices">
              <FileText className="h-4 w-4" />
              Invoices
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab - Contains both Pricing Defaults and Invoice Configuration */}
          <TabsContent value="settings" className="space-y-6">
            
            {/* ============== PRICING DEFAULTS SECTION (billing_settings) ============== */}
            <Card data-testid="pricing-defaults-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <IndianRupee className="h-5 w-5 text-primary" />
                  Pricing Defaults
                  <Badge variant="destructive" className="text-xs ml-2">Required for WhatsApp</Badge>
                </CardTitle>
                <CardDescription>
                  These fees are used when scheduling sessions via WhatsApp commands. You must configure these before using the scheduling feature.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {billingSettingsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {!billingSettings && (
                      <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                        <p className="text-sm text-amber-800">
                          <strong>Action Required:</strong> Configure pricing defaults to enable WhatsApp session scheduling.
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="default_session_fee">Default Therapy Session Fee (₹) *</Label>
                        <Input
                          id="default_session_fee"
                          data-testid="input-default-session-fee"
                          type="number"
                          min="1"
                          step="100"
                          value={billingSettingsForm.default_session_fee}
                          onChange={(e) => setBillingSettingsForm({ ...billingSettingsForm, default_session_fee: e.target.value })}
                          placeholder="2000"
                        />
                        <p className="text-xs text-muted-foreground">Used for regular therapy sessions (45-60 mins)</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="default_intro_fee">Default Intro Call Fee (₹) *</Label>
                        <Input
                          id="default_intro_fee"
                          data-testid="input-default-intro-fee"
                          type="number"
                          min="1"
                          step="100"
                          value={billingSettingsForm.default_intro_fee}
                          onChange={(e) => setBillingSettingsForm({ ...billingSettingsForm, default_intro_fee: e.target.value })}
                          placeholder="500"
                        />
                        <p className="text-xs text-muted-foreground">Used for discovery/intro calls (15 mins)</p>
                      </div>
                    </div>

                    <div className="flex justify-end pt-4">
                      <Button 
                        onClick={handleSaveBillingSettings}
                        disabled={savingBillingSettings}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="btn-save-pricing-defaults"
                      >
                        {savingBillingSettings ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-2" />
                        )}
                        Save Pricing Defaults
                      </Button>
                    </div>
                    
                    {billingSettings && (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <Check className="h-4 w-4 text-green-600" />
                        <p className="text-sm text-green-800">
                          Pricing configured: ₹{billingSettings.default_session_fee} (session) / ₹{billingSettings.default_intro_fee} (intro)
                        </p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* ============== INVOICE CONFIGURATION SECTION (invoice_settings) ============== */}
            <Card data-testid="invoice-configuration-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <Receipt className="h-5 w-5 text-primary" />
                  Invoice Configuration
                </CardTitle>
                <CardDescription>
                  Set up your clinic details for generating professional invoices when payments are marked complete.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {invoiceSettingsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {/* Required Fields */}
                    <div className="space-y-4">
                      <h3 className="font-medium flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Business Details
                        <Badge variant="destructive" className="text-xs">Required</Badge>
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="clinic_name">Clinic / Business Name *</Label>
                          <Input
                            id="clinic_name"
                            data-testid="input-clinic-name"
                            value={invoiceSettingsForm.clinic_name}
                            onChange={(e) => setInvoiceSettingsForm({ ...invoiceSettingsForm, clinic_name: e.target.value })}
                            placeholder="Mind Wellness Clinic"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="therapist_name">Therapist Name *</Label>
                          <Input
                            id="therapist_name"
                            data-testid="input-therapist-name"
                            value={invoiceSettingsForm.therapist_name}
                            onChange={(e) => setInvoiceSettingsForm({ ...invoiceSettingsForm, therapist_name: e.target.value })}
                            placeholder="Dr. Jane Doe"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="business_address">Business Address *</Label>
                        <Input
                          id="business_address"
                          data-testid="input-business-address"
                          value={invoiceSettingsForm.business_address}
                          onChange={(e) => setInvoiceSettingsForm({ ...invoiceSettingsForm, business_address: e.target.value })}
                          placeholder="123 Therapy Lane, Mumbai 400001"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="contact_phone">Contact Phone *</Label>
                          <Input
                            id="contact_phone"
                            data-testid="input-contact-phone"
                            value={invoiceSettingsForm.contact_phone}
                            onChange={(e) => setInvoiceSettingsForm({ ...invoiceSettingsForm, contact_phone: e.target.value })}
                            placeholder="+91 98765 43210"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="contact_email">Contact Email *</Label>
                          <Input
                            id="contact_email"
                            data-testid="input-contact-email"
                            type="email"
                            value={invoiceSettingsForm.contact_email}
                            onChange={(e) => setInvoiceSettingsForm({ ...invoiceSettingsForm, contact_email: e.target.value })}
                            placeholder="contact@clinic.com"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Optional Fields */}
                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="font-medium flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Payment Details
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="bank_name">Bank Name *</Label>
                          <Input
                            id="bank_name"
                            data-testid="input-bank-name"
                            value={invoiceSettingsForm.bank_name}
                            onChange={(e) => setInvoiceSettingsForm({ ...invoiceSettingsForm, bank_name: e.target.value })}
                            placeholder="HDFC Bank"
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="bank_account_number">Account Number *</Label>
                          <Input
                            id="bank_account_number"
                            data-testid="input-bank-account"
                            value={invoiceSettingsForm.bank_account_number}
                            onChange={(e) => setInvoiceSettingsForm({ ...invoiceSettingsForm, bank_account_number: e.target.value })}
                            placeholder="1234567890"
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="bank_ifsc">IFSC Code *</Label>
                          <Input
                            id="bank_ifsc"
                            data-testid="input-bank-ifsc"
                            value={invoiceSettingsForm.bank_ifsc}
                            onChange={(e) => setInvoiceSettingsForm({ ...invoiceSettingsForm, bank_ifsc: e.target.value })}
                            placeholder="HDFC0001234"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="upi_id">UPI ID *</Label>
                        <Input
                          id="upi_id"
                          data-testid="input-upi-id"
                          value={invoiceSettingsForm.upi_id}
                          onChange={(e) => setInvoiceSettingsForm({ ...invoiceSettingsForm, upi_id: e.target.value })}
                          placeholder="clinic@upi"
                          required
                        />
                      </div>

                      <h3 className="font-medium flex items-center gap-2 pt-4">
                        Tax & Other
                        <Badge variant="secondary" className="text-xs">Optional</Badge>
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="gst_number">GST Number (if registered)</Label>
                          <Input
                            id="gst_number"
                            data-testid="input-gst-number"
                            value={invoiceSettingsForm.gst_number}
                            onChange={(e) => setInvoiceSettingsForm({ ...invoiceSettingsForm, gst_number: e.target.value })}
                            placeholder="27AAAAA0000A1Z5"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="invoice_prefix">Invoice Prefix</Label>
                          <Input
                            id="invoice_prefix"
                            data-testid="input-invoice-prefix"
                            value={invoiceSettingsForm.invoice_prefix}
                            onChange={(e) => setInvoiceSettingsForm({ ...invoiceSettingsForm, invoice_prefix: e.target.value })}
                            placeholder="INV"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end pt-4">
                      <Button 
                        onClick={handleSaveInvoiceSettings}
                        disabled={savingInvoiceSettings}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="btn-save-invoice-settings"
                      >
                        {savingInvoiceSettings ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-2" />
                        )}
                        Save Invoice Settings
                      </Button>
                    </div>

                    {!invoiceSettings && (
                      <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-amber-600" />
                        <p className="text-sm text-amber-800">
                          Please configure invoice settings to enable automatic invoice generation when payments are marked done.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Packages Tab */}
          <TabsContent value="packages">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                    <Package className="h-5 w-5 text-primary" />
                    Session Packages
                  </CardTitle>
                  <CardDescription>
                    Create packages for advance payment plans
                  </CardDescription>
                </div>
                <Dialog open={packageDialogOpen} onOpenChange={(open) => {
                  setPackageDialogOpen(open);
                  if (!open) resetPackageForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary hover:bg-primary/90" data-testid="btn-add-package">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Package
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingPackage ? 'Edit Package' : 'Create Package'}</DialogTitle>
                      <DialogDescription>
                        Define a session package for advance payments
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="pkg_name">Package Name *</Label>
                        <Input
                          id="pkg_name"
                          data-testid="input-package-name"
                          value={packageForm.name}
                          onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                          placeholder="6-Session Package"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="session_count">Sessions *</Label>
                          <Input
                            id="session_count"
                            data-testid="input-session-count"
                            type="number"
                            min="1"
                            value={packageForm.session_count}
                            onChange={(e) => setPackageForm({ ...packageForm, session_count: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="price">Price (₹) *</Label>
                          <Input
                            id="price"
                            data-testid="input-package-price"
                            type="number"
                            min="0"
                            value={packageForm.price}
                            onChange={(e) => setPackageForm({ ...packageForm, price: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="discount">Discount %</Label>
                        <Input
                          id="discount"
                          data-testid="input-package-discount"
                          type="number"
                          min="0"
                          max="100"
                          value={packageForm.discount_percentage}
                          onChange={(e) => setPackageForm({ ...packageForm, discount_percentage: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Input
                          id="description"
                          data-testid="input-package-description"
                          value={packageForm.description}
                          onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                          placeholder="Save 10% with this package"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setPackageDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleSavePackage} disabled={savingPackage} data-testid="btn-submit-package">
                        {savingPackage && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {editingPackage ? 'Update' : 'Create'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {packagesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : packages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No packages created yet</p>
                    <p className="text-sm mt-1">Create packages to offer advance payment plans</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activePackages.map((pkg) => (
                      <div
                        key={pkg.package_id}
                        className="p-4 border rounded-xl hover:border-primary/50 transition-colors"
                        data-testid={`package-card-${pkg.package_id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium">{pkg.name}</h4>
                            <p className="text-sm text-muted-foreground">{pkg.session_count} sessions</p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEditPackage(pkg)}
                              data-testid={`btn-edit-package-${pkg.package_id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleDeletePackage(pkg.package_id)}
                              data-testid={`btn-delete-package-${pkg.package_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-semibold text-primary">₹{pkg.price.toLocaleString()}</span>
                          {pkg.discount_percentage > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {pkg.discount_percentage}% off
                            </Badge>
                          )}
                        </div>
                        {pkg.description && (
                          <p className="text-sm text-muted-foreground mt-2">{pkg.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <FileText className="h-5 w-5 text-primary" />
                  Generated Invoices
                </CardTitle>
                <CardDescription>
                  View and manage all generated invoices
                </CardDescription>
              </CardHeader>
              <CardContent>
                {invoicesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No invoices generated yet</p>
                    {!invoiceSettings ? (
                      <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                        <AlertCircle className="h-4 w-4 inline mr-2" />
                        <strong>Setup required:</strong> Configure your Invoice Settings in the Settings tab first. 
                        Invoices cannot be generated without business details.
                      </div>
                    ) : (
                      <p className="text-sm mt-1">
                        Invoices are automatically generated when you mark session payments as done.
                        <br />
                        Go to Sessions → Mark Payment Done to generate your first invoice.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Search/Filter Input */}
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Search by patient name or invoice number..."
                        value={invoiceSearchQuery}
                        onChange={(e) => setInvoiceSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="invoice-search-input"
                      />
                      <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      {invoiceSearchQuery && (
                        <button
                          onClick={() => setInvoiceSearchQuery('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          data-testid="invoice-search-clear"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    
                    {/* Results count */}
                    {invoiceSearchQuery && (
                      <p className="text-sm text-muted-foreground">
                        Showing {filteredInvoices.length} of {invoices.length} invoices
                      </p>
                    )}
                    
                    {/* Invoice list */}
                    <div className="space-y-3">
                    {filteredInvoices.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No invoices match "{invoiceSearchQuery}"</p>
                      </div>
                    ) : filteredInvoices.map((invoice) => (
                      <div
                        key={invoice.invoice_id}
                        className="flex items-center justify-between p-4 border rounded-xl hover:border-primary/50 transition-colors"
                        data-testid={`invoice-row-${invoice.invoice_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{invoice.invoice_number}</span>
                              <Badge variant={invoice.invoice_type === 'package' ? 'secondary' : 'outline'}>
                                {invoice.invoice_type}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {invoice.patient_name} • ₹{invoice.total_amount.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 mr-4">
                            <div className={`w-2 h-2 rounded-full ${invoice.email_sent ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <div className={`w-2 h-2 rounded-full ${invoice.whatsapp_sent ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <MessageSquare className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadInvoice(invoice.invoice_id, invoice.invoice_number)}
                            data-testid={`btn-download-invoice-${invoice.invoice_id}`}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResendInvoice(invoice.invoice_id)}
                            data-testid={`btn-resend-invoice-${invoice.invoice_id}`}
                          >
                            <Mail className="h-4 w-4 mr-1" />
                            Resend
                          </Button>
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default BillingPage;
